import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { fetchUserProfile } from "./store/slices/userSlice";
import Layout from "./components/layout/Layout";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageLoadingSkeleton } from "./components/common/LoadingSkeleton";

// Lazy load pages for better performance
const HomePage = lazy(() => import("./pages/auth/HomePage"));
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const StandaloneRegisterPage = lazy(() => import("./pages/auth/StandaloneRegisterPage"));
const ConfirmEmailPage = lazy(() => import("./pages/auth/ConfirmEmailPage"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const ActivitiesPage = lazy(() => import("./pages/activities/ActivitiesPage"));
const ActivityDetailPage = lazy(() => import("./pages/activities/ActivityDetailPage"));
const RoutesPage = lazy(() => import("./pages/routes/RoutesPage"));
const RouteDetailPage = lazy(() => import("./pages/routes/RouteDetailPage"));
const CoursesPage = lazy(() => import("./pages/courses/CoursesPage"));
const MapPage = lazy(() => import("./pages/map/MapPage"));
const StatisticsPage = lazy(() => import("./pages/statistics/StatisticsPage"));
const PredictorPage = lazy(() => import("./pages/predictor/PredictorPage"));
const AnalyserPage = lazy(() => import("./pages/analyser/AnalyserPage"));
const ProfilePage = lazy(() => import("./pages/profile/ProfilePage"));
const UsageDashboardPage = lazy(() => import("./pages/usage/UsageDashboardPage"));
const AdminPage = lazy(() => import("./pages/admin/AdminPage"));
const UnauthorizedPage = lazy(() => import("./pages/errors/UnauthorizedPage"));
const NotFoundPage = lazy(() => import("./pages/errors/NotFoundPage"));

const PublicActivityPage = lazy(() => import("./pages/activities/PublicActivityPage"));
const PrivacyPage = lazy(() => import("./pages/errors/PrivacyPage"));
const RegisterCallbackPage = lazy(() => import("./pages/auth/RegisterCallbackPage"));
const RegisterCompletePage = lazy(() => import("./pages/auth/RegisterCompletePage"));
const ErrorPage = lazy(() => import("./pages/errors/ErrorPage"));
const SetupDevice = lazy(() =>
  import("./pages/setup/SetupDevice").then((m) => ({ default: m.SetupDevice }))
);

function App() {
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const { profile } = useAppSelector((state) => state.user);

  // Fetch profile on app initialization if we have a token but no profile
  useEffect(() => {
    if (token && !profile) {
      dispatch(fetchUserProfile());
    }
  }, [dispatch, token, profile]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Layout>
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Routes>
              {/* Public routes */}
              <Route path="/activities/public/:public_token" element={<PublicActivityPage />} />
              <Route path="/setup/:device_code" element={<SetupDevice />} />
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/signup" element={<StandaloneRegisterPage />} />
              <Route path="/confirm-email" element={<ConfirmEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/analyser" element={<AnalyserPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/register/callback" element={<RegisterCallbackPage />} />
              <Route path="/register/complete" element={<RegisterCompletePage />} />
              <Route path="/error" element={<ErrorPage />} />

              {/* Protected routes */}
              <Route
                path="/activities"
                element={
                  <ProtectedRoute>
                    <ActivitiesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/activities/:id"
                element={
                  <ProtectedRoute>
                    <ActivityDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/routes"
                element={
                  <ProtectedRoute>
                    <RoutesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/routes/:id"
                element={
                  <ProtectedRoute>
                    <RouteDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses"
                element={
                  <ProtectedRoute>
                    <CoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute>
                    <MapPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/stats" element={<Navigate to="/statistics" replace />} />
              <Route
                path="/statistics"
                element={
                  <ProtectedRoute>
                    <StatisticsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/predictor" element={<PredictorPage />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usage"
                element={
                  <ProtectedRoute requireFeature="usage">
                    <UsageDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireFeature="usage">
                    <AdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Redirect /home to /activities */}
              <Route path="/home" element={<Navigate to="/activities" replace />} />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Layout>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
